import { SkedPreview } from '@dialogueconsulting/sked-preview'
import { format } from 'date-fns'
import { useState } from 'react'
import { useCollapse } from 'react-collapsed'
import styled from 'styled-components'

import type { ConversationProps, PostActionProps } from 'Components'
import { Conversation, PostAction } from 'Components'
import type { PlatformIconProps } from 'Components/Common/PlatformIcon'
import { FlexAlignCenter } from 'Components/CommonStyleComponents'
import GlobalStyle from 'GlobalStyle'
import { datePlaceholderForDraftPost } from 'normalizer'

import { StatusLabel } from '../post'

export interface MobileUpcomingPostCardProps {
  id: string
  status: string
  time: string
  when?: string
  platform?: PlatformIconProps['platform']
  skedPreviewPost: any
  conversation: ConversationProps
  postAction: PostActionProps
  isReadOnly: boolean
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
}

const MobileUpcomingPostCard = (props: MobileUpcomingPostCardProps) => {
  const {
    id,
    status,
    time,
    when = '',
    platform,
    skedPreviewPost,
    conversation,
    postAction,
    isReadOnly,
    updatePostState,
  } = props
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse({
    defaultExpanded: true,
  })

  const isDisplayTime =
    status !== 'requestChange' &&
    time !== datePlaceholderForDraftPost &&
    !isReadOnly

  let formattedDate = ''
  if (when) {
    formattedDate = format(new Date(when), 'EEEE, do LLLL HH:mm aa ')
  }

  // local status that don't persist after user refresh the page
  const [localPostStatus, setLocalPostStatus] = useState<string>('pending')

  const handleUpdatePostState = (key: any, postId: any, postStatus: any) => {
    setLocalPostStatus?.(postStatus)
    updatePostState(key, postId, postStatus)
  }

  return (
    <Card isExpanded={isExpanded}>
      <div>
        <div {...getToggleProps()}>
          <StatusLabelWrap>
            <StatusLabelStyled
              postId={id}
              status={status}
              platform={platform}
              time={time}
              isMobile={true}
              isReadOnly={isReadOnly}
              isDisplayTime={isDisplayTime}
              isExpanded={isExpanded}
              updatePostState={handleUpdatePostState}
            />
          </StatusLabelWrap>
        </div>

        <CardBody {...getCollapseProps()}>
          {status !== 'pending' && <DisabledOverlay />}
          {skedPreviewPost && (
            <>
              <GlobalStyle />
              <DateTime>{formattedDate}</DateTime>
              {
                <SkedPreviewStyled
                  post={skedPreviewPost}
                  className={undefined}
                  env={undefined}
                  trackEvent={undefined}
                  isReadOnly={false}
                  videoCoverPreview={undefined}
                />
              }
            </>
          )}

          {!isReadOnly && (
            <PostActionStyled
              {...postAction}
              postId={id}
              isMobile
              localPostStatus={localPostStatus}
            />
          )}

          {!isReadOnly && (
            <>
              <Conversation {...conversation} isMobile status={status} />

              {!postAction.isLoading && (
                <Linebreaker isStatusPending={status === 'pending'} />
              )}
            </>
          )}
        </CardBody>
      </div>
    </Card>
  )
}

const StatusLabelStyled = styled(StatusLabel)`
  width: 100%;
`
const DateTime = styled(FlexAlignCenter)`
  width: 100%;
  justify-content: center;
  height: 36px;
  padding-bottom: 4px;
`

const PostActionStyled = styled(PostAction)`
  margin: 10px 0;
`

const Card = styled.div<{ isExpanded?: boolean }>`
  min-height: 38px;
  border-radius: 5px;
  border: 1px solid ${(props) => (props.isExpanded ? '#DEDAFE' : '#eeeeee')};
  background: ${(props) => (props.isExpanded ? '#FBFBFF' : '#ffffff')};
  position: relative;
  margin-bottom: 16px;
`

const StatusLabelWrap = styled(FlexAlignCenter)`
  align-items: center;
  height: 36px;
  padding-right: 20px;

  > div:first-child {
    top: -1px;
    left: -1px;
  }
`

const CardBody = styled.div`
  padding: 8px;
`

const SkedPreviewStyled = styled(SkedPreview)<{ isReadOnly: boolean }>`
  border-radius: 10px;
  padding: 3px;
  box-shadow: 3px 0 10px rgba(0, 0, 0, 0.15);
  background: #ffffff;
  margin: ${({ isReadOnly }) => (isReadOnly ? '0' : '0 auto 18px')};
`

// belong to disabled overlay: height: calc(100% - 36px); //36px is StatusLabelWrap height
const DisabledOverlay = styled.div`
  position: absolute;
  background: #ffffff;
  width: 100%;
  z-index: 1;
  border-radius: 5px;
  opacity: 0.4;
  left: 0;
`

const Linebreaker = styled.hr<{
  isStatusPending: boolean
}>`
  border: none;

  ${(props) =>
    props.isStatusPending &&
    `
    border-bottom: 1px solid #dedafe;
    margin: 12px auto 16px;
  `}
`

export default MobileUpcomingPostCard
